'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useAuthContext } from '@/components/providers/AuthProvider';
// Footer renders minimal navigation helpers; no chat customization here

interface FooterProps {
  message?: string;
}

export default function Footer({ message = "Built by artists, for artists." }: FooterProps) {
  const { isAuthenticated } = useAuthContext();
  return (
    <footer className="relative bg-gray-800 text-slate-300 dark:bg-gray-900 py-5 md:py-6">
      <div className="container mx-auto px-6 text-center md:text-left">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-5">
          <div>
            <h3 className="text-base font-semibold text-white mb-2">Indiestry</h3>
            <p className="text-sm">Where Artists and Opportunities Meet</p>
          </div>
          <div>
            <h3 className="text-base font-semibold text-white mb-2">Navigate</h3>
            <ul className="space-y-1 text-sm">
              <li><Link href="/" className="hover:text-white hover:underline transition-colors">Home</Link></li>
              <li><Link href="/about" className="hover:text-white hover:underline transition-colors">About</Link></li>
              <li><Link href="/jobs" className="hover:text-white hover:underline transition-colors">Jobs</Link></li>
              <li><Link href="/workshops" className="hover:text-white hover:underline transition-colors">Workshops</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-base font-semibold text-white mb-2">Resources</h3>
            <ul className="space-y-1 text-sm">
              <li><Link href="/posts" className="hover:text-white hover:underline transition-colors">Community</Link></li>
              <li><Link href="/contact" className="hover:text-white hover:underline transition-colors">Contact</Link></li>
              <li><Link href="/faq" className="hover:text-white hover:underline transition-colors">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-base font-semibold text-white mb-2">Legal</h3>
            <ul className="space-y-1 text-sm">
              <li><Link href="/terms-of-service" className="hover:text-white hover:underline transition-colors">Terms of Service</Link></li>
              <li><Link href="/privacy-policy" className="hover:text-white hover:underline transition-colors">Privacy Policy</Link></li>
              <li><Link href="/community-guidelines" className="hover:text-white hover:underline transition-colors">Community Guidelines</Link></li>
              {/* Streaming Terms hidden until Phase 2 */}
              {/* <li><Link href="/streaming-terms" className="hover:text-white hover:underline transition-colors">Streaming Terms</Link></li> */}
              <li><Link href="/report-issue" className="hover:text-white hover:underline transition-colors">Report Issue</Link></li>
              <li><Link href="/report-bug" className="hover:text-white hover:underline transition-colors">Report a bug</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-600 pt-3 flex flex-col items-center md:flex-row md:justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 mb-1 md:mb-0">
            <Link href="/" aria-label="Go to home" className="inline-flex items-center">
              {/* Smaller than 40px so the copyright row reads as a line of
                  metadata instead of a band as tall as the column above. */}
              <Image
                src="/indiestrylogofull.png"
                alt="Indiestry"
                width={28}
                height={28}
                className="h-7 w-7"
                priority
              />
            </Link>
            <span className="text-slate-400" suppressHydrationWarning>&copy; {new Date().getFullYear()} Indiestry</span>
          </div>
          <p className="text-slate-500 text-center md:text-left">{message}</p>
          {!isAuthenticated && (
            <div className="flex items-center gap-2">
              <Link href="/" className="px-2.5 py-1 rounded-md bg-white/10 text-white hover:bg-white/20 transition-colors">Home</Link>
              <Link href="/login" className="px-2.5 py-1 rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors">Log In</Link>
              <Link href="/signup" className="px-2.5 py-1 rounded-md bg-white text-[#0B0B3B] hover:bg-gray-100 transition-colors">Sign Up</Link>
            </div>
          )}
        </div>
      </div>
      {/* Indiestry logo in the corner - only for non-authenticated users */}
      {!isAuthenticated && (
        <Link href="/contact" className="absolute bottom-2 right-4 cursor-pointer group" aria-label="Contact us">
          <Image 
            src="/indiestrylogofull.png" 
            alt="Indiestry - click to contact us" 
            width={140} 
            height={40} 
            className="h-10 w-auto drop-shadow-xl animate-bounce group-hover:animate-pulse transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-2xl rounded-full object-contain" 
          />
        </Link>
      )}
    </footer>
  );
} 